import React from 'react';
import { StoryblokCommon } from '../../../lib/storyblok/storyblok';
import {
    NavigationItemBannerStoryblok,
    NavigationItemOneStoryblok,
    NavigationItemTopStoryblok,
    NavigationItemTwoStoryblok,
    NavigationLinkStoryblok,
    TopNavigationStoryblok,
} from '../../../_shared/interfaces/storyblok';
import NavigationItem from './navigation-item';
import NavigationBanner from './navigation-banner';

import * as StylesHs from './navigation-menu-desktop.hs.module.scss';
import * as StylesDr from './navigation-menu-desktop.dr.module.scss';
import NavigationCustomer from './navigation-customer';
import { IAccountContext } from '../../../context/account/account.context';

const NavigationMenuDesktop = ({
    blok,
    navigationItems,
    storyblokCommon,
    accountContext,
    navigationId = 'navigation',
}: {
    blok: NavigationItemTopStoryblok | TopNavigationStoryblok | undefined;
    navigationItems?: (NavigationItemTopStoryblok | NavigationLinkStoryblok)[];
    storyblokCommon: StoryblokCommon;
    accountContext: IAccountContext;
    navigationId?: string;
}) => {
    const [activeNavigationItem, _setActiveNavigationItem] = React.useState<string[]>([]);
    const styles = storyblokCommon.configuration.content.theme === 'dr' ? StylesDr : StylesHs;
    const activeNavigationItemRef = React.useRef(activeNavigationItem);

    const setActiveNavigationItem = (uid: string, addToPosition: number) => {
        if (!activeNavigationItem.includes(uid)) {
            activeNavigationItem.length = addToPosition;
            activeNavigationItemRef.current = [...activeNavigationItem, uid];
        } else {
            activeNavigationItem.length = addToPosition;
            activeNavigationItemRef.current = [...activeNavigationItem];
        }
        _setActiveNavigationItem(activeNavigationItemRef.current);
    };

    const showNavigationItem = (navigationItem: NavigationItemTopStoryblok): React.ReactFragment => {
        const { navigationItemTop, navigationItemOne, navigationItemTwo, navigationItemBanner } = getActiveNavigationItems(navigationItem);

        return (
            <>
                {navigationItemTop && (
                    <div id={navigationId} className={styles.grid} role="menu">
                        <div id={navigationId + '-links'} className={navigationItemTop ? styles.gridItemOptions : ''}>
                            <NavigationItem
                                blok={navigationItemTop}
                                storyblokCommon={storyblokCommon}
                                activeNavigationItem={activeNavigationItem[1]}
                                showLinksOnly={true}
                                navigationId={navigationId}
                                onNavigationItemChange={(e) => setActiveNavigationItem(e._uid ?? '', getIndexOfNavigationItem(e))}
                            />
                        </div>
                        <div id={navigationId + '-top'} className={navigationItemTop ? styles.gridItemOptions : ''}>
                            <NavigationItem
                                blok={navigationItemTop}
                                storyblokCommon={storyblokCommon}
                                activeNavigationItem={activeNavigationItem[1]}
                                navigationId={navigationId}
                                onNavigationItemChange={(e) => setActiveNavigationItem(e._uid ?? '', getIndexOfNavigationItem(e))}
                            />
                        </div>
                        {navigationItemTop.navigation_items.map((item) => {
                            return (
                                <div key={item._uid} id={navigationId + '-' + item._uid} className={styles.gridHidden}>
                                    <NavigationItem
                                        key={item._uid}
                                        blok={item}
                                        storyblokCommon={storyblokCommon}
                                        navigationId={navigationId}
                                    />
                                </div>
                            );
                        })}
                        {navigationItemOne && (
                            <div id={navigationId + '-one'} className={navigationItemOne ? styles.gridItemOptions : ''}>
                                <NavigationItem
                                    blok={navigationItemOne}
                                    storyblokCommon={storyblokCommon}
                                    activeNavigationItem={activeNavigationItem[2]}
                                    navigationId={navigationId}
                                    onNavigationItemChange={(e) => setActiveNavigationItem(e._uid ?? '', getIndexOfNavigationItem(e))}
                                />
                            </div>
                        )}
                        <div
                            id={navigationId + '-banner'}
                            className={`${navigationItemBanner ? styles.gridBannerOptions : ''} ${
                                !navigationItemOne ? styles.gridBannerWide : ''
                            }`}
                        >
                            <NavigationBanner blok={navigationItemBanner} storyblokCommon={storyblokCommon} wide={!navigationItemOne} />
                        </div>
                    </div>
                )}
                {navigationItems &&
                    navigationItems.map((item) => {
                        if (item.component === 'navigation_item_top' && item._uid !== navigationItem._uid) {
                            return (
                                <div key={item._uid + '-prendered-item'}>
                                    <div id={item._uid} className={styles.gridHidden} role="menu">
                                        <NavigationItem blok={item} storyblokCommon={storyblokCommon} showLinksOnly={true} />
                                    </div>
                                    {item.navigation_items.map((currentNavigationItem) => {
                                        return (
                                            <div
                                                key={currentNavigationItem._uid}
                                                id={'megamenu-mobile-' + currentNavigationItem._uid}
                                                className={styles.gridHidden}
                                            >
                                                <NavigationItem
                                                    key={currentNavigationItem._uid}
                                                    blok={currentNavigationItem}
                                                    storyblokCommon={storyblokCommon}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        }
                    })}
            </>
        );
    };

    const showNavigationCustomer = (navigationItem: TopNavigationStoryblok): React.ReactFragment => {
        return (
            <>
                {navigationItem && (
                    <div id={navigationId} className={styles.gridRight} role="menu">
                        <div id={navigationId + '-top-'}>
                            <NavigationCustomer blok={navigationItem} accountContext={accountContext} storyblokCommon={storyblokCommon} />
                        </div>
                    </div>
                )}
            </>
        );
    };

    const getActiveNavigationItems = (
        navigationItem: NavigationItemTopStoryblok,
    ): {
        navigationItemTop: NavigationItemTopStoryblok | undefined;
        navigationItemOne: NavigationItemOneStoryblok | undefined;
        navigationItemTwo: NavigationItemTwoStoryblok | undefined;
        navigationItemBanner: NavigationItemBannerStoryblok | undefined;
    } => {
        const navigationItemOneActive = activeNavigationItem[1];
        const navigationItemTwoActive = activeNavigationItem[2];

        let navigationItemTop: NavigationItemTopStoryblok | undefined;
        let navigationItemOne: NavigationItemOneStoryblok | undefined;
        let navigationItemTwo: NavigationItemTwoStoryblok | undefined;
        let navigationItemBanner: NavigationItemBannerStoryblok | undefined;

        navigationItemTop = navigationItem;
        navigationItemBanner = navigationItem.navigation_banner && navigationItem.navigation_banner[0];

        if (navigationItemOneActive && navigationItemTop) {
            navigationItemOne = navigationItemTop.navigation_items.find((x) => {
                return x._uid === navigationItemOneActive;
            }) as NavigationItemOneStoryblok | undefined;

            if (navigationItemTwoActive && navigationItemOne) {
                navigationItemTwo = navigationItemOne.navigation_items.find((x) => {
                    return x._uid === navigationItemTwoActive && x.component !== 'navigation_sub_link';
                }) as NavigationItemTwoStoryblok | undefined;
            }
        }

        return { navigationItemTop, navigationItemOne, navigationItemTwo, navigationItemBanner };
    };

    const getIndexOfNavigationItem = (
        navigationItem: NavigationItemTopStoryblok | NavigationItemOneStoryblok | NavigationItemTwoStoryblok,
    ): number => {
        switch (navigationItem.component) {
            case 'navigation_item_top': {
                return 0;
            }
            case 'navigation_item_one': {
                return 1;
            }
            case 'navigation_item_two': {
                return 2;
            }
        }
    };

    return (
        <>
            {blok && (
                <div className={styles.navigationMenuDesktop} id={blok._uid} role="none">
                    {blok.component !== 'top_navigation' ? showNavigationItem(blok) : showNavigationCustomer(blok)}
                </div>
            )}
        </>
    );
};

export default NavigationMenuDesktop;
